 
/**
 * AdultHeroStrip.js — Phase A · #1
 * Pinned charcoal strip that replaces the playful "Today's Challenge"
 * default for adult learners. Surfaces the user's next real-world
 * conversation (manual entry now, calendar integration P2) and routes
 * them straight into the relevant AvA scenario prep.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Calendar, ArrowRight, Sparkles, Plus } from 'lucide-react';
import { apiClient } from '../../api/client';

const SUGGESTED_PROMPTS = [
  { title: 'Salary review prep',     scenario: 'salary-negotiation', icon: Briefcase },
  { title: 'Difficult 1:1 with boss', scenario: 'difficult-1-1',     icon: Briefcase },
  { title: 'Investor pitch dry-run',  scenario: 'investor-pitch',    icon: Sparkles },
  { title: 'Boundary-setting talk',   scenario: 'boundary-setting',  icon: Sparkles },
];

const AdultHeroStrip = () => {
  const navigate = useNavigate();
  const [next, setNext] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [title, setTitle] = useState('');
  const [when, setWhen] = useState('');

  useEffect(() => {
    try {
      const raw = localStorage.getItem('hibff_adult_next_event');
      if (raw) setNext(JSON.parse(raw));
    } catch (e) { /* */ }
  }, []);

  const save = () => {
    if (!title.trim()) return;
    const evt = { title: title.trim(), when: when.trim() || 'soon', created_at: Date.now() };
    try { localStorage.setItem('hibff_adult_next_event', JSON.stringify(evt)); } catch (e) { /* */ }
    setNext(evt);
    setShowAdd(false);
    setTitle(''); setWhen('');
    // Fire-and-forget telemetry — non-blocking.
    apiClient.request('/api/users/telemetry', {
      method: 'POST',
      body: JSON.stringify({ event: 'adult_next_event_set', context: evt }),
    }).catch(() => { /* */ });
  };

  return (
    <div
      className="mb-6 rounded-2xl p-6 sm:p-7 shadow-xl"
      style={{
        background: 'linear-gradient(135deg,#1F2937 0%,#0F172A 60%,#1E293B 100%)',
        color: '#F8FAFC',
      }}
      data-testid="adult-hero-strip"
    >
      <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-col sm:flex-row">
        <div className="w-12 h-12 rounded-2xl bg-amber-500/20 border border-amber-300/40 flex items-center justify-center flex-shrink-0">
          <Calendar className="text-amber-300" size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-amber-300/80">Next High-Stakes Conversation</p>
          {next ? (
            <>
              <h2 className="text-xl sm:text-2xl font-bold mt-1 leading-tight">{next.title}</h2>
              <p className="text-sm text-slate-300 mt-1">{next.when}</p>
            </>
          ) : (
            <>
              <h2 className="text-xl sm:text-2xl font-bold mt-1 leading-tight">Add the conversation you&apos;re prepping for</h2>
              <p className="text-sm text-slate-300 mt-1">Salary review · investor pitch · difficult 1:1 · boundary talk</p>
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-2 sm:flex-shrink-0">
          {next ? (
            <>
              <button
                onClick={() => navigate('/conversations/new?track=wealth')}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-amber-950 font-bold text-sm transition"
                data-testid="adult-hero-prep-now"
              >
                Prep Now <ArrowRight size={14} />
              </button>
              <button
                onClick={() => { localStorage.removeItem('hibff_adult_next_event'); setNext(null); }}
                className="px-3 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white/70 text-sm font-semibold transition"
                data-testid="adult-hero-clear"
              >
                Clear
              </button>
            </>
          ) : (
            <button
              onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-amber-950 font-bold text-sm transition"
              data-testid="adult-hero-add-event"
            >
              <Plus size={16} /> Add Event
            </button>
          )}
        </div>
      </div>

      {/* Quick-pick scenarios — one-tap jump into AvA Wealth/Health prep */}
      <div className="mt-5 flex flex-wrap gap-2" data-testid="adult-hero-scenarios">
        {SUGGESTED_PROMPTS.map((p) => {
          const Icon = p.icon;
          return (
            <button
              key={p.scenario}
              onClick={() => navigate(`/conversations/new?scenario=${p.scenario}`)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.12] text-xs font-semibold text-slate-100 border border-white/10 transition"
              data-testid={`adult-quick-${p.scenario}`}
            >
              <Icon size={12} className="text-amber-300/90" />
              {p.title}
            </button>
          );
        })}
      </div>

      {/* Add-event modal */}
      {showAdd && (
        <div className="fixed inset-0 z-[9000] bg-black/60 flex items-center justify-center p-4" data-testid="adult-add-event-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-gray-900">
            <h3 className="font-bold text-lg mb-1">What conversation are you prepping for?</h3>
            <p className="text-sm text-gray-500 mb-4">Lands on your dashboard with a one-tap prep button.</p>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Salary review with Sarah"
              className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-amber-500 outline-none mb-3"
              data-testid="adult-add-title"
            />
            <input
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              placeholder="When? (e.g. Thursday 3pm)"
              className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-amber-500 outline-none mb-4"
              data-testid="adult-add-when"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100">Cancel</button>
              <button onClick={save} disabled={!title.trim()}
                className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-amber-950 text-sm font-bold"
                data-testid="adult-add-save">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdultHeroStrip;
